import { useState } from 'react';

import { BookOpen, X } from 'lucide-react';

import { RADAR_AXES } from '../../../mocks/radarNormalization';

interface HelpPanelProps {
  open: boolean;
  onClose: () => void;
}

const AboutScoringPopup = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[60]" onClick={onClose} />
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[560px] max-h-[80vh] bg-greyP5 border border-greyP4 rounded-2xl z-[70] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-greyP4">
          <div className="flex items-center gap-2.5">
            <BookOpen size={18} className="text-green01" />
            <h3 className="text-white text-base font-semibold">About the radar &amp; scoring methodology</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-greyP4 flex items-center justify-center text-greyP2 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <p className="text-sm text-greyP1 leading-relaxed">
            The radar visualises eight performance axes for every luminaire in your catalog. Each axis is normalised to
            a 0–100 scale so the polygon's <span className="text-white font-semibold">shape</span> is directly readable:
            a fuller shape means a stronger product.
          </p>
          <div className="rounded-lg border border-green04/40 bg-green04/10 px-4 py-3 text-xs text-greyP1 leading-relaxed">
            <span className="font-semibold text-green01">PhD note · </span>
            All eight axes follow the convention <em>higher is better</em>. The "Optimal Gap" metric is the only one
            where lower is better in raw form, and is therefore inverted (shown as{' '}
            <code className="px-1 py-0.5 rounded bg-blackP1/60">100 − gap</code>) before plotting. This is documented
            per-axis in the metric definitions and in every tooltip.
          </div>
          <div className="rounded-lg border border-greyP4 bg-blackP1/40 p-4">
            <h4 className="text-white font-semibold text-sm mb-2">Comparison modes</h4>
            <ul className="text-xs text-greyP2 space-y-1.5 list-disc list-inside">
              <li>
                <span className="text-white">Product vs Product</span> — compare any two luminaires in your catalog.
              </li>
              <li>
                <span className="text-white">Product vs Optimal</span> — pin against TAMAN's optimal distribution (100
                on every axis).
              </li>
              <li>
                <span className="text-white">Product vs Portfolio Avg</span> — compare against your own catalog mean.
              </li>
              <li>
                <span className="text-white">Product vs Market Avg</span> — compare against an anonymised
                cross-manufacturer mean.
              </li>
            </ul>
          </div>
          <p className="text-[11px] text-greyP3 italic">
            All values shown in this demo are synthetic. When the backend lands, every metric here will be computed from
            real TAMAN events (matches, applies, favorites, inquiries).
          </p>
        </div>
      </div>
    </>
  );
};

export const HelpPanel = ({ open, onClose }: HelpPanelProps) => {
  const [aboutOpen, setAboutOpen] = useState(false);

  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-greyP5 border-l border-greyP4 z-50 overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-greyP5 border-b border-greyP4 px-5 py-4 flex items-center justify-between">
          <h3 className="text-white text-lg font-semibold">Metric definitions</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-greyP4 flex items-center justify-center text-greyP2 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* "About scoring" trigger — replaces the always-visible intro paragraph */}
          <button
            onClick={() => setAboutOpen(true)}
            className="w-full flex items-center justify-between gap-3 rounded-lg border border-greyP4 bg-blackP1/40 hover:border-green01/60 hover:bg-green01/5 px-4 py-3 transition-colors text-left"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-green01/10 border border-green01/40 flex items-center justify-center flex-shrink-0">
                <BookOpen size={16} className="text-green01" />
              </div>
              <div className="min-w-0">
                <div className="text-sm text-white font-semibold">About the radar &amp; scoring</div>
                <div className="text-[11px] text-greyP2 truncate">
                  How the eight axes are normalised, the "higher is better" convention, comparison modes
                </div>
              </div>
            </div>
            <span className="text-[11px] uppercase tracking-wider text-green01 font-bold flex-shrink-0">Open</span>
          </button>

          {/* Per-axis cards — main content */}
          <div className="space-y-3">
            {RADAR_AXES.map((axis) => (
              <div key={axis.key} className="rounded-lg border border-greyP4 bg-blackP1/40 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-semibold text-sm">{axis.label}</h4>
                  {axis.inverted && (
                    <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border border-green01 text-green01">
                      Inverted
                    </span>
                  )}
                </div>
                <p className="text-xs text-greyP2 leading-relaxed">{axis.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <AboutScoringPopup open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </>
  );
};
